import { css } from 'styled-components';
import { colors, r4, radius } from '@ardoq/design-tokens';

export const aiGradientBackground = `linear-gradient(
  90deg,
  ${colors.orange500} 0%,
  ${colors.orange400} 5%,
  ${colors.orange300} 10%,
  ${colors.brand400} 30%,
  ${colors.brand500} 35%,
  ${colors.brand400} 40%,
  ${colors.orange300} 60%,
  ${colors.orange400} 65%,
  ${colors.orange500} 70%,
  ${colors.orange400} 75%,
  ${colors.brand400} 95%,
  ${colors.brand500} 100%
)`;

// purple shifted earlier for wide surfaces because users sometimes confuse
// the orange for an error state when they cant readily see the purple
export const aiGradientBackgroundEarlyPurple = `linear-gradient(
  150deg,
  ${colors.orange500} 0%,
  ${colors.orange400} 2%,
  ${colors.orange300} 4%,
  ${colors.brand400} 15%,
  ${colors.brand500} 35%,
  ${colors.brand400} 50%,
  ${colors.orange300} 60%,
  ${colors.orange400} 65%,
  ${colors.orange500} 70%,
  ${colors.orange400} 75%,
  ${colors.brand400} 92%,
  ${colors.brand500} 100%
)`;

export const aiSubtleGradientFluxCSS = (angle = 90) => `linear-gradient(
  ${angle}deg,
  ${colors.orange50} 4%,
  ${colors.orange100} 35%,
  ${colors.brand50} 76%,
  ${colors.brand200} 100%
)`;

export const aiSubtleGradientFluxLightCSS = (angle = 90) => `linear-gradient(
  ${angle}deg,
  ${colors.white950} 4%,
  ${colors.orange50} 44%,
  ${colors.brand50} 98%
)`;

export const aiSubtleGradientCSS = (angle = 90) => `linear-gradient(
  ${angle}deg,
  ${colors.orange100} 0%,
  ${colors.orange50} 33%,
  ${colors.brand50} 66%,
  ${colors.brand100} 100%
)`;

export const aiSubtleGradientDarkCSS = (angle = 90) => `linear-gradient(
  ${angle}deg,
  ${colors.orange200} 0%,
  ${colors.orange100} 33%,
  ${colors.brand100} 66%,
  ${colors.brand200} 100%
)`;

// turns the border a gradient color matching our ai style
export const aiGradientBorderCSS = css`
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 6px;
    padding: 1px;
    background: ${aiGradientBackground};
    background-size: 300% 300%;
    -webkit-mask:
      linear-gradient(${colors.white950} 0 0) content-box,
      linear-gradient(${colors.white950} 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

// used in conjunction with aiGradientBorderCss to make an even more emphasized component
// (thick ai-styled gradient outline)
export const aiGradientOutlineCSS = css`
  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: inherit;
    padding: 4px;
    background: ${aiGradientBackground};
    background-size: 300% 300%;
    opacity: 0.4;
    -webkit-mask:
      linear-gradient(${colors.white950} 0 0) content-box,
      linear-gradient(${colors.white950} 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

/**
 * @deprecated we prefer the semantic color tokens when possible
 */
export const disabledCSS = css`
  pointer-events: none;
  opacity: 0.32;
  transition: opacity 0.2s ease-in-out;
`;

export const insetFocusRingCSS = css`
  &::before {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    pointer-events: none;
    box-shadow: inset 0px 0px 0px 2px ${colors.blue400};
    border-radius: ${r4};
    z-index: 1;
  }
`;

export const FOCUS_RING_SPREAD = 3;
/** for containers of elements with focus rings. uses padding and negative margins to spread outside element bounds. */
export const getFocusRingSpreadCSS = css`
  padding: ${FOCUS_RING_SPREAD}px;
  margin: -${FOCUS_RING_SPREAD}px;
`;
export const getFocusRingCSS = (offset: number, color = colors.blue400) => `
  &::before {
    pointer-events: none;
    content: ' ';
    position: absolute;
    top: -${offset}px;
    right: -${offset}px;
    bottom: -${offset}px;
    left: -${offset}px;
    display: block;
    pointer-events: none;
    ${getFocusRingBorderCSS(color)}
    border-radius: ${r4};
    z-index: 1;
  }
`;

export const getFocusRingBorderCSS = (color = colors.blue400) => `
  box-shadow:
    inset 0px 0px 0px 1px ${colors.white950},
    0px 0px 0px ${FOCUS_RING_SPREAD}px ${color};
`;

/** This is not how we ideally want to show the focus ring, but in some situations,
 e.g. in a DatasourceTable, rendering it outside the container results in very
 poor-looking, cut, effects.*/
export const getInsetFocusRingBorderCSS = (color = colors.blue400) => `
  box-shadow:
    inset 0px 0px 0px 2px ${color};
`;

export const focusRingCSS = getFocusRingCSS(1);

export const focusableCSS = css`
  position: relative;
  &:focus-visible,
  &.focus {
    ${focusRingCSS};
  }
`;

// used for checkboxes
export const focusOutline = css`
  outline: 2px solid ${colors.blue400};
  outline-offset: -2px;
  border-radius: ${radius.small};
`;

export const hideScrollbars = css`
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ellipsisCSS = css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const multiLineEllipsisCSS = (lines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  line-clamp: ${lines};
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const basicLinkCSS = css`
  color: ${colors.textAction};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${colors.textActionHover};
  }
`;

export const errorCSS = css`
  box-shadow: inset 0px 0px 0px 1px ${colors.borderError};
  &:hover {
    box-shadow: inset 0px 0px 0px 1px ${colors.borderErrorStrong};
  }
`;

export const warningCSS = css`
  box-shadow: inset 0px 0px 0px 1px ${colors.borderWarning};
  &:hover {
    box-shadow: inset 0px 0px 0px 1px ${colors.borderWarningStrong};
  }
`;
